import {useDisclosure} from '@mantine/hooks';
import {useEffect, useMemo, useState} from 'react';

/**
 * モーダルの状態を表す型定義
 */
type ModalState = {
  opened: boolean;
  open: () => void;
  close: () => void;
};

/**
 * ref管理のモーダル状態を表す型定義
 */
type RefModalState = {
  setIsOpen: (isOpen: boolean) => void;
};

/**
 * エントリー管理画面で使用するモーダルの開閉状態を統合管理するカスタムフック
 *
 * @description
 * - useDisclosureで管理するモーダル（入力方法選択、スクリーンショット、テキスト取り込み、エントリー管理追加/編集/削除）
 * - refで管理するモーダル（ステップ編集、分析結果）の開閉状態をトラッキング
 * - すべてのモーダルが閉じた際にエントリー管理追加モードをリセット
 *
 * @note 個別のステップ削除モーダルは useProgressDeleteModal で管理
 */
export const useProgressModalStates = () => {
  // useDisclosureで管理するモーダル（直接開閉制御が可能）
  const [
    inputMethodModalOpened,
    {open: openInputMethodModal, close: closeInputMethodModal},
  ] = useDisclosure(false);

  const [
    screenshotModalOpened,
    {open: openScreenshotModal, close: closeScreenshotModal},
  ] = useDisclosure(false);

  const [
    textImportModalOpened,
    {open: openTextImportModal, close: closeTextImportModal},
  ] = useDisclosure(false);

  const [addMgtModalOpened, {open: openAddMgtModal, close: closeAddMgtModal}] =
    useDisclosure(false);

  const [
    editEntryModalOpened,
    {open: openEditEntryModal, close: closeEditEntryModal},
  ] = useDisclosure(false);

  const [
    deleteEntryModalOpened,
    {open: openDeleteEntryModal, close: closeDeleteEntryModal},
  ] = useDisclosure(false);

  const [
    overallStatusUpdateModalOpened,
    {open: openOverallStatusUpdateModal, close: closeOverallStatusUpdateModal},
  ] = useDisclosure(false);

  const [
    reportAnalyzeModalOpened,
    {open: openReportAnalyzeModal, close: closeReportAnalyzeModal},
  ] = useDisclosure(false);

  // ref管理のモーダルの開閉状態を追跡
  const [stepModalIsOpen, setStepModalIsOpen] = useState(false);
  const [analyzeResultModalIsOpen, setAnalyzeResultModalIsOpen] =
    useState(false);

  // エントリー管理追加モードのフラグ
  const [isAddingEntryManagement, setIsAddingEntryManagement] = useState(false);

  // すべてのモーダルが閉じているかをメモ化して計算
  const areAllModalsClosed = useMemo(() => {
    return (
      !inputMethodModalOpened &&
      !screenshotModalOpened &&
      !textImportModalOpened &&
      !stepModalIsOpen &&
      !analyzeResultModalIsOpen &&
      !addMgtModalOpened &&
      !editEntryModalOpened &&
      !deleteEntryModalOpened &&
      !overallStatusUpdateModalOpened &&
      !reportAnalyzeModalOpened
    );
  }, [
    inputMethodModalOpened,
    screenshotModalOpened,
    textImportModalOpened,
    stepModalIsOpen,
    analyzeResultModalIsOpen,
    addMgtModalOpened,
    editEntryModalOpened,
    deleteEntryModalOpened,
    overallStatusUpdateModalOpened,
    reportAnalyzeModalOpened,
  ]);

  // すべてのモーダルが閉じた時にisAddingEntryManagementをリセット
  useEffect(() => {
    if (isAddingEntryManagement && areAllModalsClosed) {
      setIsAddingEntryManagement(false);
    }
  }, [isAddingEntryManagement, areAllModalsClosed]);

  return {
    // モーダルの開閉状態
    modals: {
      inputMethodModal: {
        opened: inputMethodModalOpened,
        open: openInputMethodModal,
        close: closeInputMethodModal,
      } satisfies ModalState,
      screenshotModal: {
        opened: screenshotModalOpened,
        open: openScreenshotModal,
        close: closeScreenshotModal,
      } satisfies ModalState,
      textImportModal: {
        opened: textImportModalOpened,
        open: openTextImportModal,
        close: closeTextImportModal,
      } satisfies ModalState,
      addMgtModal: {
        opened: addMgtModalOpened,
        open: openAddMgtModal,
        close: closeAddMgtModal,
      } satisfies ModalState,
      editEntryModal: {
        opened: editEntryModalOpened,
        open: openEditEntryModal,
        close: closeEditEntryModal,
      } satisfies ModalState,
      deleteEntryModal: {
        opened: deleteEntryModalOpened,
        open: openDeleteEntryModal,
        close: closeDeleteEntryModal,
      } satisfies ModalState,
      overallStatusUpdateModal: {
        opened: overallStatusUpdateModalOpened,
        open: openOverallStatusUpdateModal,
        close: closeOverallStatusUpdateModal,
      } satisfies ModalState,
      reportAnalyzeModal: {
        opened: reportAnalyzeModalOpened,
        open: openReportAnalyzeModal,
        close: closeReportAnalyzeModal,
      } satisfies ModalState,
      // ref管理モーダル用のsetters
      stepModal: {
        setIsOpen: setStepModalIsOpen,
      } satisfies RefModalState,
      analyzeResultModal: {
        setIsOpen: setAnalyzeResultModalIsOpen,
      } satisfies RefModalState,
    },
    // エントリー管理追加モードのフラグ
    isAddingEntryManagement,
    setIsAddingEntryManagement,
    // ヘルパー関数
    areAllModalsClosed,
  };
};
